import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.normalizeScroll?.(true);

const SECTION_IDS = ['homeHero', 'homeBlock1', 'homeBlock2', 'homeBlock3', 'homeBlock4'];
const DEFAULT_DURATION = 33;

let activeInstance = null;

function getCandidateSections() {
    return SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
}

function createTrack(hero, sections, videoWrapper) {
    const parent = hero.parentElement;
    if (!parent) return null;

    const existingTrack = hero.closest('[data-home-video-track]');
    if (existingTrack) return existingTrack;

    const track = document.createElement('div');
    track.className = 'home-video-track';
    track.dataset.homeVideoTrack = 'true';

    parent.insertBefore(track, hero);
    if (videoWrapper) {
        track.appendChild(videoWrapper);
    }
    sections.forEach((section) => track.appendChild(section));

    return track;
}


function initHomeVideo(root = document) {
    const hero = root.querySelector('[data-home-hero]');
    if (!hero) return;

    const videoWrapper = hero.querySelector('[data-home-video]');
    if (!videoWrapper) return;
    const video = videoWrapper.querySelector('video');
    if (!video) return;

    if (activeInstance) {
        activeInstance.destroy();
        activeInstance = null;
    }

    const sections = getCandidateSections();
    if (sections.length < SECTION_IDS.length) return;

    const track = createTrack(hero, sections, videoWrapper);
    if (!track) return;

    const instance = createController({ video, videoWrapper, sections, track });
    instance.start();
    activeInstance = instance;
}

function destroyHomeVideo() {
    if (!activeInstance) return;
    activeInstance.destroy();
    activeInstance = null;
}

function createController({ video, videoWrapper, sections, track }) {
    let endObserver = null;
    let trackTrigger = null;
    let lockPosition = 0;
    let trackTop = 0;
    let scrubTimer = null;
    let handleResize = null;
    let introRaf = null;
    let introForwardRaf = null;
    let introPlaying = true;
    let lastProgress = 0;
    const INTRO_LOOP_START = 0;
    const INTRO_LOOP_END = 10;
    const INTRO_EXIT_TIME = 11;
    let scrollHandler = null;

    const buildBounds = () => {
        const last = sections[sections.length - 1];
        if (last) {
            const rect = last.getBoundingClientRect();
            const top = rect.top + window.pageYOffset;
            trackTop = track.getBoundingClientRect().top + window.pageYOffset;
            lockPosition = Math.max(0, top + rect.height - window.innerHeight);
        }
    };

    const stopLoop = () => {
        if (scrubTimer) {
            clearTimeout(scrubTimer);
            scrubTimer = null;
        }
        if (introRaf) {
            cancelAnimationFrame(introRaf);
            introRaf = null;
        }
        if (introForwardRaf) {
            cancelAnimationFrame(introForwardRaf);
            introForwardRaf = null;
        }
    };

    const getVideoEnd = () => {
        return video.duration && Number.isFinite(video.duration) ? video.duration : DEFAULT_DURATION;
    };

    const clampToTrack = (progress) => {
        const clamped = Math.min(Math.max(progress, 0), 1);
        const end = getVideoEnd();
        video.currentTime = end * clamped;
    };

    const playIntroLoop = () => {
        stopLoop();
        introPlaying = true;
        video.currentTime = Math.min(Math.max(video.currentTime, INTRO_LOOP_START), INTRO_LOOP_END);
        video.playbackRate = 1;
        video.play().catch(() => {});

        const tick = () => {
            if (!introPlaying) return;
            if (video.currentTime >= INTRO_LOOP_END - 0.03) {
                video.currentTime = INTRO_LOOP_START;
            }
            introRaf = requestAnimationFrame(tick);
        };
        introRaf = requestAnimationFrame(tick);
    };

    const exitIntroAndScrub = () => {
        if (!introPlaying) return;
        introPlaying = false;
        stopLoop();
        const tick = () => {
            if (video.currentTime >= INTRO_EXIT_TIME - 0.03) {
                video.currentTime = INTRO_EXIT_TIME;
                video.playbackRate = 1;
                video.pause();
                scrubTrack(lastProgress);
                return;
            }
            video.playbackRate = 2.6;
            video.play().catch(() => {});
            introForwardRaf = requestAnimationFrame(tick);
        };
        introForwardRaf = requestAnimationFrame(tick);
    };

    const setVideoVisible = (visible) => {
        if (!videoWrapper) return;
        videoWrapper.style.display = visible ? '' : 'none';
    };

    const updateLockState = () => {
        if (!videoWrapper) return;
        const scrollY = window.pageYOffset;
        if (scrollY >= lockPosition) {
            videoWrapper.classList.add('is-locked');
            const offset = Math.max(0, lockPosition - trackTop);
            videoWrapper.style.top = `${offset}px`;
        } else {
            videoWrapper.classList.remove('is-locked');
            videoWrapper.style.top = '';
        }
    };

    const scrubTrack = (progress) => {
        stopLoop();
        video.pause();
        clampToTrack(progress);
        if (scrubTimer) clearTimeout(scrubTimer);
        scrubTimer = setTimeout(() => {
        }, 120);
    };

    const start = () => {
        buildBounds();
        video.muted = true;
        video.playsInline = true;
        video.autoplay = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        const onReady = () => {
            clampToTrack(0);
            video.pause();
            playIntroLoop();
            ScrollTrigger.refresh();
        };
        if (video.readyState >= 2) {
            onReady();
        } else {
            video.addEventListener('loadedmetadata', onReady, { once: true });
        }

        trackTrigger = ScrollTrigger.create({
            trigger: track,
            start: 'top top',
            end: () => {
                buildBounds();
                const distance = Math.max(1, lockPosition - trackTop);
                return `+=${distance}`;
            },
            scrub: 1.2,
            onUpdate: (self) => {
                lastProgress = self.progress;
                if (self.progress <= 0.02 && self.direction < 0) {
                    if (!introPlaying) {
                        playIntroLoop();
                    }
                    return;
                }
                if (introPlaying) {
                    exitIntroAndScrub();
                    return;
                }
                scrubTrack(self.progress);
            },
        });

        handleResize = () => {
            buildBounds();
            ScrollTrigger.refresh();
        };

        window.addEventListener('resize', handleResize);
        scrollHandler = () => {
            updateLockState();
        };
        window.addEventListener('scroll', scrollHandler, { passive: true });
        updateLockState();

        const lastSection = sections[sections.length - 1];
        if (lastSection) {
            endObserver = ScrollTrigger.create({
                trigger: lastSection,
                start: 'top top',
                end: 'bottom top',
                onLeave: () => {
                    stopLoop();
                    video.currentTime = getVideoEnd();
                    video.pause();
                },
                onEnterBack: () => setVideoVisible(true),
            });
        }
    };

    const destroy = () => {
        stopLoop();
        setVideoVisible(true);
        if (handleResize) {
            window.removeEventListener('resize', handleResize);
            handleResize = null;
        }
        if (scrollHandler) {
            window.removeEventListener('scroll', scrollHandler);
            scrollHandler = null;
        }
        if (endObserver) {
            endObserver.kill();
            endObserver = null;
        }
        if (trackTrigger) {
            trackTrigger.kill();
            trackTrigger = null;
        }
        try {
            video.pause();
        } catch (error) {
            // ignore
        }
    };

    return { start, destroy };
}

export { initHomeVideo, destroyHomeVideo };
